// InlineEditableCell：列表 / 详情表格中的内联文本编辑。
// 默认显示静态文本，点击进入编辑模式；回车保存（async），Esc 取消。
// 保存中显示"…"占位，错误回滚到原值并冒泡给上层 toast。

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/Input";

export interface InlineEditableCellProps {
  value: string;
  // onSave：保存触发；reject 即视为失败，UI 回滚原值。
  onSave: (next: string) => Promise<void>;
  // 占位符（空值时显示）。
  placeholder?: string;
  // 编辑框 aria-label，给测试与可访问性。
  ariaLabel?: string;
  // 是否禁用编辑（如未登录、无写权限）。
  disabled?: boolean;
  // 单元格 className 覆盖。
  className?: string;
}

export function InlineEditableCell({
  value,
  onSave,
  placeholder = "(空)",
  ariaLabel = "编辑",
  disabled = false,
  className,
}: InlineEditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 外部 value 变化（query refetch）时同步 draft，避免显示陈旧值。
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  async function commit() {
    if (saving) return;
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      // 保存失败：回退到原值，让上层 toast 展示错误。
      setDraft(value);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value);
    setEditing(false);
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        aria-label={ariaLabel}
        className={
          className ??
          "text-left text-xs hover:underline disabled:cursor-not-allowed disabled:no-underline"
        }
      >
        {value || (
          <span className="text-[hsl(var(--muted-foreground))]">
            {placeholder}
          </span>
        )}
      </button>
    );
  }

  return (
    <Input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      disabled={saving}
      aria-label={ariaLabel}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          void commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      className="h-7 text-xs"
    />
  );
}
